/**
 * Simple Cart Manager - Version sin conflictos
 * Maneja solo el carrito drawer sin interferir con otros scripts
 */

class SimpleCartManager {
  constructor() {
    this.cart = null;
    this.isUpdating = false;
    this.init();
  }

  init() {
    console.log('SimpleCartManager: Inicializando...');
    
    // Simple event delegation for cart buttons
    document.addEventListener('click', this.handleClick.bind(this));
    
    // Listen when products are added
    document.addEventListener('product:added', () => {
      this.loadCartData(true);
    });

    // Listen when drawer opens
    document.addEventListener('openDrawer', (event) => {
      if (event.detail && event.detail.drawerId === 'cart') {
        this.loadCartData();
      }
    });

    // Load initial data
    this.loadCartData();
  }

  handleClick(event) {
    // Only handle clicks on cart buttons
    const quantityButton = event.target.closest('.quantity-button');
    if (quantityButton) {
      event.preventDefault();
      event.stopPropagation();
      this.handleQuantityChange(quantityButton);
      return;
    }

    const removeButton = event.target.closest('.cart-item-remove');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.handleRemoveItem(removeButton);
      return;
    }
  }

  async handleQuantityChange(button) {
    if (this.isUpdating) return;

    const variantId = button.dataset.variantId;
    const action = button.dataset.action;
    const quantityElement = button.parentElement.querySelector('.quantity-value');
    
    if (!variantId || !quantityElement) {
      console.error('SimpleCartManager: Elementos requeridos no encontrados');
      return;
    }

    const currentQuantity = parseInt(quantityElement.dataset.quantity) || parseInt(quantityElement.textContent);
    let newQuantity = currentQuantity;

    if (action === 'increase') {
      newQuantity = currentQuantity + 1;
    } else if (action === 'decrease') {
      newQuantity = Math.max(0, currentQuantity - 1);
    }

    console.log(`SimpleCartManager: Cambiando cantidad ${variantId}: ${currentQuantity} -> ${newQuantity}`);
    await this.updateCartItem(variantId, newQuantity);
  }

  async handleRemoveItem(button) {
    if (this.isUpdating) return;
    
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    console.log(`SimpleCartManager: Eliminando item ${variantId}`);
    await this.updateCartItem(variantId, 0);
  }

  async updateCartItem(variantId, quantity) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    this.setButtonsLoading(variantId, true);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });

      const result = await response.json();

      if (response.ok || response.status === 422) {
        // 422 significa límite de stock, pero actualizar de todas formas
        await this.loadCartData();
        
        if (response.status === 422) {
          this.showMessage(result.message || 'Stock limitado - cantidad ajustada');
        }
      } else {
        console.error('SimpleCartManager: Error updating cart:', result);
        this.showMessage('Error updating cart');
      }
    } catch (error) {
      console.error('SimpleCartManager: Network error:', error);
      this.showMessage('Connection error');
    } finally {
      this.setButtonsLoading(variantId, false);
      this.isUpdating = false;
    }
  }

  async loadCartData(openCart = false) {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      
      // Si se va a abrir el carrito (nuevo producto), recargar HTML completo
      if (openCart) {
        await this.refreshCartDrawer();
      } else {
        this.updateCartDisplay();
      }
      
      this.updateCartCount();

      if (openCart) {
        this.openCart();
      }
    } catch (error) {
      console.error('SimpleCartManager: Error loading cart:', error);
    }
  }

  async refreshCartDrawer() {
    const cartItemsContainer = document.querySelector('.cart-items');
    if (!cartItemsContainer) return;

    try {
      // Obtener el snippet del carrito actualizado
      const response = await fetch(`/cart`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extraer solo el contenido de cart-items
        const newCartItems = doc.querySelector('.cart-items');
        if (newCartItems) {
          cartItemsContainer.innerHTML = newCartItems.innerHTML;
        }
      }
    } catch (error) {
      console.error('Error refreshing drawer:', error);
      // Fallback to normal method
      this.updateCartDisplay();
    }
  }

  updateCartDisplay() {
    if (!this.cart) return;

    const cartEmpty = document.getElementById('cart-empty');
    const cartContent = document.getElementById('cart-content');
    const cartSubtotal = document.getElementById('cart-subtotal');

    // Mostrar/ocultar estado vacío
    if (this.cart.item_count === 0) {
      if (cartEmpty) cartEmpty.style.display = 'flex';
      if (cartContent) cartContent.style.display = 'none';
    } else {
      if (cartEmpty) cartEmpty.style.display = 'none';
      if (cartContent) cartContent.style.display = 'block';
      
      // Actualizar subtotal
      if (cartSubtotal) {
        cartSubtotal.textContent = this.formatMoney(this.cart.total_price);
      }

      // Actualizar cada item existente
      this.cart.items.forEach(item => {
        const variantId = item.variant_id;
        const itemElement = document.querySelector(`[data-cart-item="${variantId}"]`);
        
        if (itemElement) {
          this.updateExistingCartItem(itemElement, item);
        }
      });

      // Remover items que ya no existen
      const allCartItems = document.querySelectorAll('.cart-item[data-cart-item]');
      allCartItems.forEach(itemElement => {
        const variantId = itemElement.dataset.cartItem;
        const itemExists = this.cart.items.some(item => item.variant_id.toString() === variantId);
        
        if (!itemExists) {
          itemElement.remove();
        }
      });
    }
  }

  updateExistingCartItem(itemElement, item) {
    // Actualizar cantidad
    const quantityElement = itemElement.querySelector('.quantity-value');
    if (quantityElement) {
      quantityElement.textContent = item.quantity;
      quantityElement.dataset.quantity = item.quantity;
    }
    
    // Actualizar precio
    const priceElement = itemElement.querySelector('.cart-item-price-final');
    const priceContainer = itemElement.querySelector('.cart-item-price');
    if (priceElement) {
      const formattedPrice = this.formatMoney(item.final_line_price);
      // Quitar el símbolo de moneda ya que el template maneja $ y USD por separado
      priceElement.textContent = formattedPrice.replace(/[$€£¥₹]|USD|EUR|GBP|JPY|INR/g, '').trim();
    }
    
    // Detectar si hay descuento y aplicar clase has-sale
    if (priceContainer) {
      const hasDiscount = item.original_line_price && item.original_line_price > item.final_line_price;
      if (hasDiscount) {
        priceContainer.classList.add('has-sale');
        // Actualizar precio original si existe
        const originalPriceElement = itemElement.querySelector('.cart-item-price-original');
        if (originalPriceElement) {
          const formattedOriginalPrice = this.formatMoney(item.original_line_price);
          originalPriceElement.textContent = formattedOriginalPrice.replace(/[$€£¥₹]|USD|EUR|GBP|JPY|INR/g, '').trim();
        }
      } else {
        priceContainer.classList.remove('has-sale');
      }
    }
  }

  updateCartCount() {
    const cartCountElements = document.querySelectorAll('.cart-count');
    const itemCount = this.cart ? this.cart.item_count : 0;
    
    cartCountElements.forEach(element => {
      element.textContent = itemCount;
    });
  }

  setButtonsLoading(variantId, loading) {
    const itemElement = document.querySelector(`[data-cart-item="${variantId}"]`);
    if (!itemElement) return;

    const buttons = itemElement.querySelectorAll('.quantity-button, .cart-item-remove');
    buttons.forEach(button => {
      button.disabled = loading;
      if (loading) {
        button.classList.add('loading');
      } else {
        button.classList.remove('loading');
      }
    });
  }

  openCart() {
    const cartButton = document.querySelector('[data-drawer-toggle="cart"]');
    if (cartButton) {
      cartButton.click();
    }
  }

  formatMoney(cents) {
    if (window.formatMoney) {
      return window.formatMoney(cents);
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  showMessage(message) {
    console.log('SimpleCartManager:', message);
    
    // Mostrar mensaje simple
    const messageDiv = document.createElement('div');
    messageDiv.textContent = message;
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #333;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
    `;
    
    document.body.appendChild(messageDiv);
    
    setTimeout(() => {
      if (messageDiv.parentNode) {
        messageDiv.parentNode.removeChild(messageDiv);
      }
    }, 3000);
  }
}

// Initialize only if no other cart manager exists
document.addEventListener('DOMContentLoaded', () => {
  if (!window.unifiedCart && !window.cartManager) {
    window.cartManager = new SimpleCartManager();
    console.log('SimpleCartManager: Inicializado correctamente');
  } else {
    console.log('SimpleCartManager: Otro cart manager ya existe, saltando inicialización');
  }
});
